/**
 * Roadmap #16 + #17 — the camera registry and facility coverage, as data.
 *
 * Configuration only. Cameras are OPTIONAL: no workflow, Location Truth,
 * Birdseye or prompt reads any of this. Coverage says what a camera CAN
 * observe — never what is occupied — and NO COVERAGE ≠ EMPTY.
 *
 * Pure: no React, no Supabase.
 */

export type CameraPlacement = 'INTERIOR' | 'EXTERIOR';
export type CoverageKind = 'BAY' | 'PARKING_SPOT' | 'LOT_AREA' | 'ENTRANCE' | 'EXIT';

export interface ShopCamera {
  id: string;
  shopId: string;
  name: string;
  placement: CameraPlacement;
  observesOccupancy: boolean;
  observesTransition: boolean;
  readsPlates: boolean;
  enabled: boolean;
  archivedAt: string | null;
  /** Health EVIDENCE — written only by trusted server code. Null = none yet. */
  lastSeenAt: string | null;
  lastObservationAt: string | null;
  lastErrorAt: string | null;
  lastError: string | null;
}

export interface CoverageTarget {
  kind: CoverageKind;
  bayId?: string | null;
  parkingSpotId?: string | null;
  label?: string | null;
}

export interface CameraCoverageRow extends CoverageTarget {
  id: string;
  cameraId: string;
}

// ── Topology: derived from the registry, never stored ──────────────────────
export type CameraTopology = 'NONE' | 'INTERIOR_ONLY' | 'EXTERIOR_ONLY' | 'INTERIOR_AND_EXTERIOR';

/** Only cameras that would count: not archived AND enabled (SQL: shop_camera_topology). */
export function countsTowardTopology(c: Pick<ShopCamera, 'enabled' | 'archivedAt'>): boolean {
  return c.enabled && !c.archivedAt;
}

export function deriveCameraTopology(cameras: readonly Pick<ShopCamera, 'placement' | 'enabled' | 'archivedAt'>[]): CameraTopology {
  const live = cameras.filter(countsTowardTopology);
  const interior = live.some((c) => c.placement === 'INTERIOR');
  const exterior = live.some((c) => c.placement === 'EXTERIOR');
  if (interior && exterior) return 'INTERIOR_AND_EXTERIOR';
  if (interior) return 'INTERIOR_ONLY';
  if (exterior) return 'EXTERIOR_ONLY';
  return 'NONE';
}

export const TOPOLOGY_WORDS: Record<CameraTopology, string> = {
  NONE: 'No active cameras',
  INTERIOR_ONLY: 'Interior cameras only',
  EXTERIOR_ONLY: 'Exterior cameras only',
  INTERIOR_AND_EXTERIOR: 'Interior and exterior cameras',
};

// ── Capabilities guide what coverage means ──────────────────────────────────
/**
 * Occupancy-capable cameras map places (bays, spots, a coarse lot area);
 * transition-capable cameras map entrances / exits. Plate reading is an
 * identity capability only — it never makes a target observable by itself.
 * Guidance for the editor, not a server rule: real layouts vary.
 */
export function suggestedCoverageKinds(c: Pick<ShopCamera, 'observesOccupancy' | 'observesTransition'>): CoverageKind[] {
  return [
    ...(c.observesOccupancy ? (['BAY', 'PARKING_SPOT', 'LOT_AREA'] as const) : []),
    ...(c.observesTransition ? (['ENTRANCE', 'EXIT'] as const) : []),
  ];
}

export function capabilityWords(c: Pick<ShopCamera, 'observesOccupancy' | 'observesTransition' | 'readsPlates'>): string {
  return [
    c.observesOccupancy && 'Occupancy',
    c.observesTransition && 'Transitions',
    c.readsPlates && 'Plate reading',
  ].filter(Boolean).join(' · ');
}

// ── Coverage keys, validity, summary ────────────────────────────────────────
export function coverageKey(t: CoverageTarget): string {
  if (t.kind === 'BAY') return `BAY:${t.bayId}`;
  if (t.kind === 'PARKING_SPOT') return `PARKING_SPOT:${t.parkingSpotId}`;
  return `${t.kind}:${(t.label ?? '').trim().toLowerCase()}`;
}

/** The shop's currently mappable physical targets (active bays / spots). */
export interface CameraTargetIndex {
  bayLabels: ReadonlyMap<string, string>;
  spotLabels: ReadonlyMap<string, string>;
}

/**
 * A physical target that is no longer an active bay / spot of the shop is
 * shown as such — never treated as still observable, never remapped.
 * Virtual targets (area / entrance / exit) are always valid labels.
 */
export function coverageIsValid(t: CoverageTarget, targets: CameraTargetIndex): boolean {
  if (t.kind === 'BAY') return !!t.bayId && targets.bayLabels.has(t.bayId);
  if (t.kind === 'PARKING_SPOT') return !!t.parkingSpotId && targets.spotLabels.has(t.parkingSpotId);
  return !!t.label?.trim();
}

export function coverageTargetLabel(t: CoverageTarget, targets: CameraTargetIndex): string {
  if (t.kind === 'BAY') return targets.bayLabels.get(t.bayId ?? '') ?? 'Removed or inactive bay';
  if (t.kind === 'PARKING_SPOT') return targets.spotLabels.get(t.parkingSpotId ?? '') ?? 'Removed or inactive spot';
  const label = t.label?.trim() ?? '';
  if (t.kind === 'ENTRANCE') return `${label} — Entrance`;
  if (t.kind === 'EXIT') return `${label} — Exit`;
  return `${label} (lot area)`;
}

/** "Covers 4 bays · 18 parking spots · South Lot · Front Drive (Entrance + Exit)" */
export function coverageSummary(rows: readonly CoverageTarget[], targets: CameraTargetIndex): string {
  if (rows.length === 0) return 'Coverage not mapped';
  const valid = rows.filter((r) => coverageIsValid(r, targets));
  const stale = rows.length - valid.length;
  const bays = valid.filter((r) => r.kind === 'BAY').length;
  const spots = valid.filter((r) => r.kind === 'PARKING_SPOT').length;
  const areas = valid.filter((r) => r.kind === 'LOT_AREA').map((r) => r.label!.trim());
  const gates = new Map<string, { label: string; entrance: boolean; exit: boolean }>();
  for (const r of valid) {
    if (r.kind !== 'ENTRANCE' && r.kind !== 'EXIT') continue;
    const key = r.label!.trim().toLowerCase();
    const g = gates.get(key) ?? { label: r.label!.trim(), entrance: false, exit: false };
    if (r.kind === 'ENTRANCE') g.entrance = true; else g.exit = true;
    gates.set(key, g);
  }
  const parts = [
    bays > 0 && `${bays} ${bays === 1 ? 'bay' : 'bays'}`,
    spots > 0 && `${spots} parking ${spots === 1 ? 'spot' : 'spots'}`,
    ...areas,
    ...[...gates.values()].map((g) => `${g.label} (${g.entrance && g.exit ? 'Entrance + Exit' : g.entrance ? 'Entrance' : 'Exit'})`),
  ].filter(Boolean);
  const head = parts.length > 0 ? `Covers ${parts.join(' · ')}` : 'No valid coverage';
  return stale > 0 ? `${head} · ${stale} no longer available` : head;
}

// ── Health evidence: shown truthfully, never inferred ───────────────────────
export type CameraEvidenceState = 'NO_DATA' | 'HAS_EVIDENCE';

/**
 * There is no agent or ingest yet, so there is no online/offline threshold:
 * with no evidence the camera is "not connected yet"; with evidence, the
 * timestamps are shown as they are. Nothing here claims "Connected".
 */
export function cameraEvidenceState(c: Pick<ShopCamera, 'lastSeenAt' | 'lastObservationAt' | 'lastErrorAt'>): CameraEvidenceState {
  return c.lastSeenAt || c.lastObservationAt || c.lastErrorAt ? 'HAS_EVIDENCE' : 'NO_DATA';
}
export const NO_CONNECTION_WORDS = 'No connection data yet';

// ── Parking range selection: an editing convenience only ────────────────────
/**
 * Spots between two chosen spots in the shop's own spot order (labels are
 * not assumed numeric). The stored truth stays one assignment per spot.
 */
export function spotRange(orderedSpotIds: readonly string[], fromId: string, toId: string): string[] {
  const a = orderedSpotIds.indexOf(fromId);
  const b = orderedSpotIds.indexOf(toId);
  if (a < 0 || b < 0) return [];
  const [lo, hi] = a <= b ? [a, b] : [b, a];
  return orderedSpotIds.slice(lo, hi + 1);
}
